import { inject, ref, unref, watch, type MaybeRef } from 'vue'
import { GRID_HTTP_INJECTION_KEY } from '@zoryn/components/grid'
import type { GridColumnProps } from '@zoryn/components/grid'
import type { HttpRequest } from '@zoryn/network'

const TABLE_CONFIG_DETAIL_URL = '/system/table-config/queryDetailByPath'
const TABLE_CONFIG_QUERY_URL = '/system/table-config/query'

export interface RulesTableFieldRow {
  tableField?: string
  tableName?: string
  enable?: boolean
  columnType?: string
}

export interface RulesTableQueryColumn {
  field: string
  rawExpr?: string
  columnType?: string
}

interface ApiResult<T> {
  success?: boolean
  msg?: string
  data?: T
}

export function isDataSourcePath(path?: string): boolean {
  return Boolean(path?.startsWith('/'))
}

export function mapRulesTableFieldsToColumns(
  fields: RulesTableFieldRow[]
): GridColumnProps[] {
  return fields
    .filter((item) => item.enable !== false && item.tableField)
    .map((item) => ({
      field: item.tableField!,
      title: item.tableName ?? item.tableField!,
      width: 140,
    }))
}

export function mapRulesTableQueryColumnsToGrid(
  columns: RulesTableQueryColumn[]
): GridColumnProps[] {
  return columns
    .filter((item) => item.field)
    .map((item) => ({
      field: item.field,
      title: item.field,
      width: 140,
    }))
}

function unwrapApiData<T>(res: unknown, fallbackMsg = '请求失败'): T {
  const payload = res as ApiResult<T>
  if (payload.success === false) {
    throw new Error(payload.msg ?? fallbackMsg)
  }
  if (payload.data === undefined || payload.data === null) {
    throw new Error(fallbackMsg)
  }
  return payload.data
}

export function resolveTableRowLabel(
  rows: Record<string, unknown>[],
  valueField: string,
  labelField: string | undefined,
  value: unknown
): string | undefined {
  if (value == null || value === '') {
    return undefined
  }
  const row = rows.find((item) => item[valueField] === value)
  if (!row) {
    return undefined
  }
  if (labelField && row[labelField] != null && row[labelField] !== '') {
    return String(row[labelField])
  }
  return String(row[valueField] ?? value)
}

export function useRulesTable(dataSourcePath: MaybeRef<string | undefined>) {
  const request = inject<HttpRequest | null>(GRID_HTTP_INJECTION_KEY, null)
  const columns = ref<GridColumnProps[]>([])
  const rows = ref<Record<string, unknown>[]>([])
  const loading = ref(false)
  const error = ref<Error | null>(null)

  async function loadTableData() {
    const path = unref(dataSourcePath)
    if (!path || !isDataSourcePath(path)) {
      columns.value = []
      rows.value = []
      error.value = null
      return
    }
    if (!request) {
      columns.value = []
      rows.value = []
      error.value = new Error('[ZRules] 未配置 HttpRequest')
      return
    }

    loading.value = true
    error.value = null
    try {
      const detailRes = await request.get({
        url: TABLE_CONFIG_DETAIL_URL,
        params: { path },
      })
      const detail = unwrapApiData<{
        tableConfig?: Record<string, unknown>
        fields?: RulesTableFieldRow[]
      }>(detailRes)

      const fieldColumns = mapRulesTableFieldsToColumns(detail.fields ?? [])

      const queryRes = await request.post({
        url: TABLE_CONFIG_QUERY_URL,
        params: { path, pageSize: 500 },
      })
      const queryData = unwrapApiData<{
        columns?: RulesTableQueryColumn[]
        rows?: Record<string, unknown>[]
      }>(queryRes)

      rows.value = Array.isArray(queryData.rows) ? queryData.rows : []
      if (fieldColumns.length) {
        columns.value = fieldColumns
      } else {
        columns.value = mapRulesTableQueryColumnsToGrid(queryData.columns ?? [])
      }
    } catch (err) {
      columns.value = []
      rows.value = []
      error.value = err instanceof Error ? err : new Error(String(err))
    } finally {
      loading.value = false
    }
  }

  watch(
    () => unref(dataSourcePath),
    (path) => {
      if (!path || !isDataSourcePath(path)) {
        columns.value = []
        rows.value = []
        error.value = null
      }
    }
  )

  return {
    columns,
    rows,
    loading,
    error,
    reload: loadTableData,
  }
}
